import { defineStore } from 'pinia'
import { computed } from 'vue'
import { isManagedByAgency } from '@/modules/onboarding/services/managedAgencyOnboarding'
import { useAuthStore } from '@/stores/authStore'
import { useConfigStore } from '@/stores/configStore'
export const useOnboardingStore = defineStore('onboarding', () => {
  const configStore = useConfigStore()
  const { fullResponse } = storeToRefs(configStore)
  const authStore = useAuthStore()
  const { user } = storeToRefs(authStore)

  const managedByAgency = computed(() => isManagedByAgency(user.value))


  const integrationStore = useIntegrationStore()
  const { partners, integratedPartner } = storeToRefs(integrationStore)
  const hasIntegrated = computed(() => {
    if (!partners.value.length) return false
    return integratedPartner.value
  })

  const reportStore = useReportStore()
  const { dataLastReceived } = storeToRefs(reportStore)
  const hasAddedScript = computed(() => {
    if (!dataLastReceived.value) return true
    return dataLastReceived.value?.last_update !== null
  })

  // hasAddedScript is optimistic while the reporting query is still in
  // flight (avoids flashing install banners at existing users). That makes
  // it unsafe for anything persisted — launch plan items seeded or
  // auto-completed from it would record a false positive forever. Use this
  // resolved-only signal for persisted completion state.
  const hasConfirmedScript = computed(
    () => !!dataLastReceived.value && dataLastReceived.value.last_update !== null
  )

  const flowStore = useFlowStore()
  const { flows } = storeToRefs(flowStore)
  const hasCreatedFlow = computed(
    () => flows.value.filter((f) => visibleThingsInFlow(f.funnelDefinition).length > 0).length > 0
  )

  const campaignStore = useCampaignStore()
  const { campaigns } = storeToRefs(campaignStore)
  const hasActiveCampaign = computed(
    () => campaigns.value.filter((campaign) => campaign.is_active).length > 0
  )

  const widgetStore = useWidgetStore()
  const { widgets } = storeToRefs(widgetStore)
  const hasCreatedWidget = computed(
    () => widgets.value.filter((widget) => widget.contentId && widget.isEnabled).length > 0
  )

  // Don't count the initial auto-publish (blank config) as a real publish.
  // A real publish means the stable config has meaningful content.
  const hasPublished = computed(() => {
    const response = fullResponse.value
    if (!response || typeof response !== 'object' || Array.isArray(response)) return false

    const responseRecord = response as Record<string, unknown>
    const stable = responseRecord.stable
    if (!stable || typeof stable !== 'object' || Array.isArray(stable)) return false

    const stableConfig = stable as {
      dimensions?: unknown
      campaigns?: unknown
      rightCta?: { offerFunnels?: Array<{ funnelDefinition?: unknown }> } | null
    }
    const hasDimensions = Array.isArray(stableConfig.dimensions) && stableConfig.dimensions.length > 0
    const hasCampaigns = Array.isArray(stableConfig.campaigns) && stableConfig.campaigns.length > 0
    const hasFlows = Array.isArray(stableConfig.rightCta?.offerFunnels) && stableConfig.rightCta.offerFunnels.some(
      (funnel) =>
        !!funnel.funnelDefinition &&
        typeof funnel.funnelDefinition === 'object' &&
        visibleThingsInFlow(
          funnel.funnelDefinition as { type: 'root'; _uuid?: string; child?: unknown }
        ).length > 0
    )

    return hasDimensions || hasCampaigns || hasFlows
  })

  const hasMeaningfulFirstWinProgress = computed(() => {
    return !!(
      hasPublished.value ||
      (hasConfirmedScript.value &&
        (hasCreatedFlow.value || hasCreatedWidget.value || hasActiveCampaign.value))
    )
  })

  const shouldShowDashboardExperience = computed(() => {
    if (managedByAgency.value) return true
    if (!partners.value.length) return true
    return hasMeaningfulFirstWinProgress.value
  })

  const shouldShowDashboardOnboardingFallback = computed(
    () => !managedByAgency.value && !shouldShowDashboardExperience.value
  )

  return {
    managedByAgency,
    shouldShowDashboardExperience,
    shouldShowDashboardOnboardingFallback,
    hasIntegrated,
    hasAddedScript,
    hasConfirmedScript,
    hasCreatedFlow,
    hasActiveCampaign,
    hasCreatedWidget,
    hasPublished,
    hasMeaningfulFirstWinProgress
  }
})
